import { graphql } from 'react-relay';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import type {
	fallbackMediaNameFetcherQuery$data,
	fallbackMediaNameFetcherQuery$variables,
} from './__generated__/fallbackMediaNameFetcherQuery.graphql.ts';

const PAGE_SIZE = 50;
// setting max iterations to avoid ever getting into an infinite loop just in case
const MAX_ITERATIONS = 50;

const fallbackMediaNameFetcherQueryNode = graphql`
	query fallbackMediaNameFetcherQuery($contentId: ID!, $offset: Int, $first: Int) {
		content(id: $contentId, status: ["current", "draft"]) {
			edges {
				node {
					attachments(first: $first, offset: $offset) {
						nodes {
							title
							extensions {
								key
								value
							}
						}
						pageInfo {
							hasNextPage
						}
					}
				}
			}
		}
	}
`;

const ANALYTICS_SOURCE = 'fallbackMediaNameFetcher';

type MediaProviderWithRename = {
	renameFile?: (fileId: string, name: string) => Promise<void>;
};

type FallbackMediaNameFetcherOptions = {
	mediaProvider?: Promise<MediaProviderWithRename>;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
};

/**
 * Creates a fallbackMediaNameFetcher for MediaInlineCard that resolves the attachment
 * filename from Confluence GraphQL using the media file UUID.
 *
 * Paginates through all attachments on the page (PAGE_SIZE at a time) until the
 * matching fileId is found, short-circuiting as soon as a match is found.
 * Uses cache-first so Apollo deduplicates concurrent in-flight requests and caches
 * already-fetched pages within the same session.
 *
 * Once the filename is found, calls the Media API PUT /files/rename to
 * rename the file so future lookups resolve directly from the media service.
 *
 * Fires an operational event each time the fetcher is called, recording whether
 * the file was found and how many pages were scanned.
 *
 * Used as a workaround for #hot-301450 where DC -> Cloud migrated media files
 * are missing filenames in the media service.
 *
 * Gated behind the `platform_editor_media_name_fallback` experiment at the call site.
 */
export const createFallbackMediaNameFetcher =
	(
		contentId: string,
		{ mediaProvider, createAnalyticsEvent }: FallbackMediaNameFetcherOptions = {},
	): ((id: string) => Promise<string>) =>
	async (id: string): Promise<string> => {
		let offset = 0;
		let iterations = 0;
		let hasNextPage = true;

		while (hasNextPage && iterations < MAX_ITERATIONS) {
			const { data } = await queryInterop<
				fallbackMediaNameFetcherQuery$data,
				fallbackMediaNameFetcherQuery$variables
			>({
				query: fallbackMediaNameFetcherQueryNode,
				variables: { contentId, offset, first: PAGE_SIZE },
				fetchPolicy: 'cache-first',
			}).catch((e) => {
				if (isUnauthorizedError(e) || containsNetworkError(e)) {
					markErrorAsHandled(e);
				}
				return { data: null };
			});

			const attachments = data?.content?.edges?.[0]?.node?.attachments;
			const nodes = attachments?.nodes ?? [];

			for (const node of nodes) {
				const fileId = node?.extensions?.find((ext) => ext?.key === 'fileId')?.value;
				if (fileId === id && node?.title) {
					const fileName = node.title;
					if (expValEquals('platform_editor_media_file_rename_on_fallback', 'isEnabled', true)) {
						createAnalyticsEvent?.({
							type: 'sendOperationalEvent',
							data: {
								action: 'resolved',
								actionSubject: 'mediaFileName',
								source: ANALYTICS_SOURCE,
							},
						})?.fire();
						if (mediaProvider) {
							void mediaProvider
								.then((provider) => provider.renameFile?.(id, fileName))
								.catch((error: unknown) => {
									createAnalyticsEvent?.({
										type: 'sendOperationalEvent',
										data: {
											action: 'failed',
											actionSubject: 'mediaFileRename',
											source: ANALYTICS_SOURCE,
											attributes: {
												errorMessage:
													error instanceof Error ? error.message : 'Error renaming file',
											},
										},
									})?.fire();
								});
						}
					}
					return fileName;
				}
			}

			hasNextPage = attachments?.pageInfo?.hasNextPage ?? false;
			offset += PAGE_SIZE;
			iterations += 1;
		}

		if (expValEquals('platform_editor_media_file_rename_on_fallback', 'isEnabled', true)) {
			createAnalyticsEvent?.({
				type: 'sendOperationalEvent',
				data: {
					action: 'notFound',
					actionSubject: 'mediaFileName',
					source: ANALYTICS_SOURCE,
					attributes: {
						pagesScanned: iterations,
					},
				},
			})?.fire();
		}

		throw new Error(`Attachment with fileId ${id} not found on page ${contentId}`);
	};
